/**
 * HubSpot Deals integration
 * Client-side helper to create deals via Edge Function
 */

import { supabase } from '@/integrations/supabase/client';

export interface CreateDealParams {
  unitId: string;
  contactId: string; // HubSpot contact ID
  leadSource?: Record<string, unknown>;
  engagementScore?: number;
  leadQuality?: 'Hot' | 'Warm' | 'Cold';
}

export interface CreateDealResponse {
  success: boolean;
  dealId?: string;
  error?: string;
}

/**
 * Map unit availability status to HubSpot deal stage
 * Using actual stage IDs from HubSpot portal 50726975 default pipeline
 */
export function mapUnitStatusToDealStage(status: 'available' | 'reserved' | 'sold'): string {
  const stageMap: Record<string, string> = {
    available: '1225024819',  // First stage in pipeline (Appointment Scheduled / Qualified to Buy)
    reserved: '1225024822',   // Contract Sent stage
    sold: 'closedwon',        // Closed Won
  };

  return stageMap[status] || '1225024819';
}

/**
 * Create a HubSpot Deal via Edge Function
 * This calls the Supabase Edge Function which creates the deal in HubSpot
 */
export async function createHubSpotDeal(
  params: CreateDealParams
): Promise<CreateDealResponse> {
  try {
    if (import.meta.env.DEV) {
      console.log('📤 Invoking sync-hubspot-deal Edge Function:', {
        unitId: params.unitId,
        contactId: params.contactId,
        leadQuality: params.leadQuality,
        engagementScore: params.engagementScore
      });
    }

    const { data, error } = await supabase.functions.invoke('sync-hubspot-deal', {
      body: {
        unitId: params.unitId,
        contactId: params.contactId,
        leadSource: params.leadSource || {},
        engagementScore: params.engagementScore || 0,
        leadQuality: params.leadQuality || 'Cold',
      },
    });

    if (import.meta.env.DEV) {
      console.log('📥 sync-hubspot-deal response:', { data, error });
    }

    if (error) {
      console.error('❌ Error calling sync-hubspot-deal Edge Function:', error);
      return {
        success: false,
        error: error.message || 'Failed to create HubSpot deal',
      };
    }

    if (data?.success) {
      return {
        success: true,
        dealId: data.dealId,
      };
    } else {
      return {
        success: false,
        error: data?.error || 'Unknown error creating HubSpot deal',
      };
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error creating HubSpot deal';
    if (import.meta.env.DEV) {
      console.error('❌ Exception in createHubSpotDeal:', error);
    } else {
      console.error('Error creating HubSpot deal:', error);
    }
    return {
      success: false,
      error: errorMessage,
    };
  }
}

/**
 * Get HubSpot Contact ID by email address
 * Queries HubSpot Contacts API via Edge Function with retry logic
 */
export async function getContactIdByEmail(
  email: string,
  retries: number = 3,
  delay: number = 2000
): Promise<string | null> {
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      const { data, error } = await supabase.functions.invoke('get-hubspot-contact', {
        body: { email },
      });

      if (error) {
        if (import.meta.env.DEV) {
          console.warn(`Attempt ${attempt}/${retries} - Error calling get-hubspot-contact:`, error);
        }
        
        // If not the last attempt, wait and retry
        if (attempt < retries) {
          await new Promise(resolve => setTimeout(resolve, delay * attempt));
          continue;
        }
        
        console.error('Error calling get-hubspot-contact Edge Function:', error);
        return null;
      }

      if (data?.success && data?.contactId) {
        if (import.meta.env.DEV && attempt > 1) {
          console.log(`✅ Contact ID retrieved on attempt ${attempt}:`, data.contactId);
        }
        return String(data.contactId);
      }

      // Contact not found - might need to wait longer
      if (data?.success && !data?.contactId) {
        if (import.meta.env.DEV) {
          console.warn(`Attempt ${attempt}/${retries} - Contact not found for email:`, email);
        }
        
        // If not the last attempt, wait and retry (contact might not be created yet)
        if (attempt < retries) {
          await new Promise(resolve => setTimeout(resolve, delay * attempt));
          continue;
        }
        
        return null;
      }

      // Unknown response format
      if (import.meta.env.DEV) {
        console.warn('Unexpected response from get-hubspot-contact:', data);
      }
      return null;
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn(`Attempt ${attempt}/${retries} - Error getting contact ID:`, error);
      }
      
      // If not the last attempt, wait and retry
      if (attempt < retries) {
        await new Promise(resolve => setTimeout(resolve, delay * attempt));
        continue;
      }
      
      console.error('Error getting contact ID by email:', error);
      return null;
    }
  }

  return null;
}

/**
 * Extract contact ID from HubSpot form submission response
 * NOTE: HubSpot Forms API v3 does NOT return contact ID in response
 * Use getContactIdByEmail() instead after form submission
 * @deprecated Use getContactIdByEmail() instead
 */
export function getContactIdFromFormResponse(response: unknown): string | null {
  try {
    // HubSpot Forms API response structure
    // Response may be in different formats depending on API version
    if (typeof response === 'object' && response !== null) {
      const obj = response as Record<string, unknown>;
      
      // Try different possible response formats
      if (obj.contactId) {
        return String(obj.contactId);
      }
      if (obj.inlineMessage) {
        // Sometimes contact ID is in inlineMessage
        const message = String(obj.inlineMessage);
        const match = message.match(/contact[_-]?id[:\s]+(\d+)/i);
        if (match) {
          return match[1];
        }
      }
      if (obj.redirectUri) {
        // Sometimes contact ID is in redirect URI
        const uri = String(obj.redirectUri);
        const match = uri.match(/contact[_-]?id[=:](\d+)/i);
        if (match) {
          return match[1];
        }
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error extracting contact ID from HubSpot response:', error);
    return null;
  }
}

